import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, Award, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import appSound from '@/services/app-sound-service';
import { apiRequest } from '@/lib/queryClient';

export interface Achievement {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  category: string;
  requirement?: number;
  progress?: number;
  reward?: {
    amount: number;
    currencyType: 'regular' | 'premium';
  };
}

interface AchievementsDialogProps {
  achievements: Achievement[];
  isOpen: boolean;
  onClose: () => void;
}

const ACHIEVEMENTS_SEEN_KEY = 'spar-achievements-seen';

export function AchievementsDialog({ achievements, isOpen, onClose }: AchievementsDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentAchievement = achievements[currentIndex];
  const hasMoreAchievements = currentIndex < achievements.length - 1;
  
  // Filter already seen achievements from localStorage
  useEffect(() => {
    if (isOpen && achievements.length > 0) {
      // Check for already seen achievements
      const seenAchievementsString = localStorage.getItem(ACHIEVEMENTS_SEEN_KEY);
      let seenAchievements: number[] = [];
      
      if (seenAchievementsString) {
        try {
          seenAchievements = JSON.parse(seenAchievementsString);
        } catch (e) {
          console.error('Error parsing seen achievements:', e);
        }
      }

      // Play sound effect when dialog opens
      appSound.playAchievementRewardSound();
    }
  }, [isOpen, achievements.length]);

  const markAchievementAsSeen = async (achievementId: number) => {
    try {
      // Save to localStorage first
      const seenAchievementsString = localStorage.getItem(ACHIEVEMENTS_SEEN_KEY);
      let seenAchievements: number[] = [];
      
      if (seenAchievementsString) {
        try {
          seenAchievements = JSON.parse(seenAchievementsString);
        } catch (e) {
          console.error('Error parsing seen achievements:', e);
        }
      }
      
      // Add this achievement ID if not already in the list
      if (!seenAchievements.includes(achievementId)) {
        seenAchievements.push(achievementId);
        localStorage.setItem(ACHIEVEMENTS_SEEN_KEY, JSON.stringify(seenAchievements));
      }
      
      // Also mark as seen in the backend
      await apiRequest('POST', '/api/achievements/seen', { achievementId });
    } catch (error) {
      console.error('Error marking achievement as seen:', error);
    }
  };

  const handleNext = () => {
    // Mark current achievement as seen
    if (currentAchievement && currentAchievement.id) {
      markAchievementAsSeen(currentAchievement.id);
    }
    
    if (hasMoreAchievements) {
      // Play sound effect when moving to next achievement
      appSound.playButtonSound();
      setCurrentIndex(currentIndex + 1);
    } else {
      handleClose();
    }
  };
  
  const handleClose = () => {
    // Mark all remaining achievements as seen when closing
    if (achievements.length > 0) {
      // Mark current achievement as seen
      if (currentAchievement && currentAchievement.id) {
        markAchievementAsSeen(currentAchievement.id);
      }
      
      // If the dialog is closed before seeing all achievements, mark the rest as seen too
      if (currentIndex < achievements.length - 1) {
        achievements.slice(currentIndex + 1).forEach(achievement => {
          if (achievement.id) {
            markAchievementAsSeen(achievement.id);
          }
        });
      }
    }
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-black/95 border-yellow-500/30">
        <DialogHeader className="flex justify-between items-center">
          <div className="flex items-center gap-2 text-yellow-500">
            <Trophy className="h-5 w-5" />
            <h2 className="text-xl font-bold">Achievement Unlocked!</h2>
          </div>
          
        </DialogHeader>
        
        {achievements.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentAchievement.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center py-4"
            >
              <div className="relative w-32 h-32 mb-6">
                <div className="absolute inset-0 bg-yellow-500/20 rounded-full animate-ping-slow opacity-75"></div>
                <div className="relative z-10 w-full h-full bg-black/70 rounded-full p-4 flex items-center justify-center border-2 border-yellow-500">
                  <img
                    src={currentAchievement.imageUrl || "/achievements/default.png"}
                    alt={currentAchievement.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center mb-2 text-white">
                {currentAchievement.name}
              </h3>
              
              <p className="text-center text-gray-300 mb-6">
                {currentAchievement.description}
              </p>

              {currentAchievement.reward && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3 mb-6 flex items-center gap-3">
                  <Award className="h-5 w-5 text-yellow-500" />
                  <span>
                    <span className="font-semibold text-yellow-500">+{currentAchievement.reward.amount}</span>
                    <span className="text-yellow-500/80"> {currentAchievement.reward.currencyType === 'premium' ? 'Gems' : 'Coins'}</span>
                  </span>
                </div>
              )}

              <Button
                onClick={handleNext}
                className="mt-4 w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold"
              >
                {hasMoreAchievements ? (
                  <>
                    Next Achievement <ChevronRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  "Close"
                )}
              </Button>
              
              {achievements.length > 1 && (
                <div className="flex items-center justify-center gap-1 mt-4">
                  {achievements.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 rounded-full transition-all ${
                        index === currentIndex
                          ? "w-6 bg-yellow-500"
                          : "w-1.5 bg-yellow-500/30"
                      }`}
                    ></div>
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </DialogContent>
    </Dialog>
  );
}